import React, { useState } from "react";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { loginSuccess } from "../../features/auth/authSlice";
import { setPermissions } from "../../features/permissions/permissionSlice";
import { loginRequest } from "../../api/authAPI";
import logo from "../../Containers/Pictures/logo.png";
import styles from "./Login.module.css";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { getRandomQuote } from "../../Util/quotes";
import Button from "../../Components/Button/Button.js";
import useReducedMotion from "../../hooks/useReducedMotion.js";

function Login() {
    const dispatch = useDispatch();
    const history = useHistory();

    const [username,    setUsername]    = useState("");
    const [password,    setPassword]    = useState("");
    const [showPwd,     setShowPwd]     = useState(false);
    const [load,        setLoad]        = useState(false);
    const [initParticles, setInitParticles] = useState(false);
    // Decorative background: off for reduced motion.
    const reducedMotion = useReducedMotion();
    const [dailyQuote,    setDailyQuote]    = useState(null);

    React.useEffect(() => {
        setDailyQuote(getRandomQuote());
        initParticlesEngine(async (engine) => {
            await loadSlim(engine);
        }).then(() => setInitParticles(true));
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error("Please enter your username and password.");
            return;
        }

        setLoad(true);
        const toastId = toast.loading("Signing in…");

        try {
            const result = await loginRequest(username, password);

            const accessToken       = result?.accessToken;
            const refreshExpiresAt  = result?.refreshExpiresAt;
            const user              = result?.user;
            const permissions       = result?.permissions;

            dispatch(loginSuccess({ token: accessToken, refreshExpiresAt, user }));
            dispatch(setPermissions(permissions));

            toast.dismiss(toastId);

            if (permissions?.includes("dashboard:view")) {
                history.push("/dashboard");
            } else {
                history.push("/");
            }
            setTimeout(
                () => toast.success(`Welcome back, ${user?.username ?? username}!`),
                150,
            );
        } catch (err) {
            toast.dismiss(toastId);
            const message =
                err?.message ??
                err?.response?.data?.message ??
                "Invalid credentials. Please try again.";
            toast.error(message);
        } finally {
            setLoad(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* ── Particle Background ───────────────────── */}
            {initParticles && !reducedMotion && (
                <Particles
                    id="tsparticles"
                    className={styles.particlesContainer}
                    options={{
                        background: { color: { value: "transparent" } },
                        fpsLimit: 60, // shared/older terminals: decoration must stay cheap
                        interactivity: {
                            events: {
                                onClick: { enable: true, mode: "push" },
                                onHover: { enable: true, mode: "grab" },
                            },
                            modes: {
                                push: { quantity: 4 },
                                grab: { distance: 140, links: { opacity: 0.8 } },
                            },
                        },
                        particles: {
                            color: { value: "#c84535" },
                            links: {
                                color: "#e8725f",
                                distance: 150,
                                enable: true,
                                opacity: 0.3,
                                width: 1,
                            },
                            move: {
                                direction: "none",
                                enable: true,
                                outModes: { default: "bounce" },
                                random: false,
                                speed: 1.5,
                                straight: false,
                            },
                            number: { density: { enable: true, area: 800 }, value: 120 },
                            opacity: { value: 0.6 },
                            shape: { type: "circle" },
                            size: { value: { min: 1, max: 3 } },
                        },
                        detectRetina: true,
                    }}
                />
            )}

            {/* ── Watermark logo ──────────────────────── */}
            <img src={logo} alt="" aria-hidden className={styles.watermark} />

            {/* ── Daily Quote ─────────────────────────── */}
            {dailyQuote && (
                <div className={styles.quoteWrap}>
                    <p className={styles.quoteText}>"{dailyQuote.text}"</p>
                    <p className={styles.quoteAuthor}>— {dailyQuote.author}</p>
                </div>
            )}

            <div className={styles.card}>
                <h1 className={styles.title}>Welcome back</h1>
                <p className={styles.sub}>Sign in to your account</p>

                <form onSubmit={onSubmit} autoComplete="on" className={styles.form}>
                    {/* Username */}
                    <div className={styles.field}>
                        <label htmlFor="username" className={styles.label}>Username</label>
                        <input
                            id="username"
                            name="username"
                            type="text"
                            autoComplete="username"
                            className={styles.input}
                            placeholder="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value.toUpperCase())}
                            disabled={load}
                            autoFocus
                            required
                        />
                    </div>

                    {/* Password with eye toggle */}
                    <div className={styles.field}>
                        <label htmlFor="password" className={styles.label}>Password</label>
                        <div className={styles.passwordWrap}>
                            <input
                                id="password"
                                name="password"
                                type={showPwd ? "text" : "password"}
                                autoComplete="current-password"
                                className={styles.input}
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={load}
                                required
                            />
                        <button
                                type="button"
                                className={styles.eyeBtn}
                                onClick={() => setShowPwd((v) => !v)}
                                tabIndex={-1}
                                aria-label={showPwd ? "Hide password" : "Show password"}
                            >
                                {showPwd ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        variant="primary"
                        size="xl"
                        fullWidth
                        className={styles.btn}
                        disabled={load}
                    >
                        {load ? "Signing in…" : "Sign in"}
                    </Button>
                </form>

                {/* Link to dedicated change-password page */}
                <div className={styles.changePwdRow}>
                    <a href="/change-password" className={styles.changePwdLink}>
                        Change password
                    </a>
                </div>
            </div>
        </div>
    );
}

export default Login;
